import type { EventCourseSubCategoryTypeId } from '@/lib/event-course-category';
import { isNavigationPillar, NAVIGATION_PILLARS, type NavigationPillar } from '@/lib/navigation-pillars';

export const EVENT_COURSE_CATEGORY_COUNTS_SCHEMA_VERSION = 2;

export type EventCourseSubcategoryCount = {
    categoryId: string;
    name: string;
    productCount: number;
    url?: string | null;
    imageUrl?: string | null;
    eventCourseSubCategoryType?: string | null;
};

export type EventCourseCityCount = {
    categoryId: string;
    name: string;
    productCount: number;
    url?: string | null;
    imageUrl?: string | null;
    eventCourseSubCategoryType?: string | null;
    subcategories: EventCourseSubcategoryCount[];
};

export type EventCoursePillarCount = {
    categoryId: string;
    name: string;
    productCount: number;
    url?: string | null;
    imageUrl?: string | null;
    eventCourseSubCategoryType?: string | null;
    cities: EventCourseCityCount[];
};

export type EventCourseCategoryCounts = {
    schemaVersion: number;
    generatedAt: string;
    siteId: string;
    /** Keyed by navigation pillar (`events`, `courses`), not by the site-specific root category id. */
    pillars: Partial<Record<NavigationPillar, EventCoursePillarCount>>;
};

export type EventCourseSubcategoryLocation = {
    categoryId: string;
    name: string;
    imageUrl: string | null;
    href: string;
    eventCourseSubCategoryType: string;
};

export type EventCourseCityLookup = {
    pillar: NavigationPillar;
    city: EventCourseCityCount;
};

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isOptionalStringOrNull(value: unknown): boolean {
    return value === undefined || value === null || typeof value === 'string';
}

function isSubcategoryCount(value: unknown): value is EventCourseSubcategoryCount {
    if (!isRecord(value)) {
        return false;
    }
    return (
        typeof value.categoryId === 'string' &&
        typeof value.name === 'string' &&
        typeof value.productCount === 'number' &&
        isOptionalStringOrNull(value.url) &&
        isOptionalStringOrNull(value.imageUrl) &&
        isOptionalStringOrNull(value.eventCourseSubCategoryType)
    );
}

function isCityCount(value: unknown): value is EventCourseCityCount {
    if (!isRecord(value) || !Array.isArray(value.subcategories)) {
        return false;
    }
    return (
        typeof value.categoryId === 'string' &&
        typeof value.name === 'string' &&
        typeof value.productCount === 'number' &&
        isOptionalStringOrNull(value.url) &&
        isOptionalStringOrNull(value.imageUrl) &&
        isOptionalStringOrNull(value.eventCourseSubCategoryType) &&
        value.subcategories.every(isSubcategoryCount)
    );
}

function isPillarCount(value: unknown): value is EventCoursePillarCount {
    if (!isRecord(value) || !Array.isArray(value.cities)) {
        return false;
    }
    return (
        typeof value.categoryId === 'string' &&
        typeof value.name === 'string' &&
        typeof value.productCount === 'number' &&
        isOptionalStringOrNull(value.url) &&
        isOptionalStringOrNull(value.imageUrl) &&
        isOptionalStringOrNull(value.eventCourseSubCategoryType) &&
        value.cities.every(isCityCount)
    );
}

export function parseEventCourseCategoryCountsPayload(raw: unknown): EventCourseCategoryCounts | undefined {
    if (typeof raw !== 'string' || raw.length === 0) {
        return undefined;
    }

    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch {
        return undefined;
    }

    if (!isRecord(parsed) || parsed.schemaVersion !== EVENT_COURSE_CATEGORY_COUNTS_SCHEMA_VERSION) {
        return undefined;
    }
    if (typeof parsed.generatedAt !== 'string' || typeof parsed.siteId !== 'string' || !isRecord(parsed.pillars)) {
        return undefined;
    }

    const pillars: EventCourseCategoryCounts['pillars'] = {};
    for (const [key, value] of Object.entries(parsed.pillars)) {
        if (!isNavigationPillar(key) || !isPillarCount(value)) {
            return undefined;
        }
        pillars[key] = value;
    }

    return {
        schemaVersion: parsed.schemaVersion,
        generatedAt: parsed.generatedAt,
        siteId: parsed.siteId,
        pillars,
    };
}

/**
 * Cities on the current events/courses pillar with a subcategory that has items.
 * Image comes from the city; href is the subcategory URL.
 */
export function getAllEventCourseSubcategoryLocations(
    counts: EventCourseCategoryCounts | undefined,
    pillar: NavigationPillar | null | undefined
): EventCourseSubcategoryLocation[] {
    const cities = pillar ? counts?.pillars[pillar]?.cities : undefined;
    if (!cities?.length) {
        return [];
    }

    const locations: EventCourseSubcategoryLocation[] = [];
    for (const city of cities) {
        for (const subcategory of city.subcategories) {
            if (!subcategory.eventCourseSubCategoryType || subcategory.productCount <= 0 || !subcategory.url) {
                continue;
            }
            locations.push({
                categoryId: city.categoryId,
                name: city.name,
                imageUrl: city.imageUrl ?? null,
                href: subcategory.url,
                eventCourseSubCategoryType: subcategory.eventCourseSubCategoryType,
            });
        }
    }
    return locations;
}

/**
 * Cities on the current events/courses pillar that have items in a subcategory of the given type.
 * Image comes from the city; href is the matching subcategory URL.
 */
export function getLocationsForEventCourseSubcategoryType(
    counts: EventCourseCategoryCounts | undefined,
    pillar: NavigationPillar | null | undefined,
    type: EventCourseSubCategoryTypeId
): EventCourseSubcategoryLocation[] {
    return getAllEventCourseSubcategoryLocations(counts, pillar).filter(
        (location) => location.eventCourseSubCategoryType === type
    );
}

/** Finds a city (store) category under any events/courses pillar. */
export function getEventCourseCityByCategoryId(
    counts: EventCourseCategoryCounts | undefined,
    categoryId: string | undefined
): EventCourseCityLookup | undefined {
    const id = categoryId?.trim();
    if (!counts || !id) {
        return undefined;
    }

    for (const pillar of NAVIGATION_PILLARS) {
        const city = counts.pillars[pillar]?.cities.find((entry) => entry.categoryId === id);
        if (city) {
            return { pillar, city };
        }
    }

    return undefined;
}
